import type { CsvContents, ResponseError } from '@rtkwlf/rendall';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import type { ColumnInstance } from 'react-table';

import { useRendallApi } from '@apis/rendall/use-rendall-api';
import { useOrganizationContext } from '@packages/legacy/organization-context';
import { convertToTitleCase, isEmpty } from '@packages/utils/base.utils';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';

type TicketEventsParamType = { ticketId: string; attachmentId: string };
type TicketEventsSelectType<SelectedData> = (
  data: CsvContents,
  t: TFunction,
) => SelectedData;

function useTicketEvents<SelectedData>(
  params: TicketEventsParamType,
  t: TFunction,
  select: TicketEventsSelectType<SelectedData>,
): UseQueryResult<SelectedData, ResponseError> {
  const api = useRendallApi();
  const { selectedOrganization } = useOrganizationContext();
  const { ticketId, attachmentId } = params;

  return useQuery({
    queryFn: () =>
      api.getCsvTicketAttachment({
        attachmentId,
        organizationId: selectedOrganization.id,
        ticketId,
      }),
    queryKey: ticketKeys.events(
      selectedOrganization.id,
      ticketId,
      attachmentId,
    ),
    select: (data: CsvContents) => select(data, t),
  });
}

type NormalizeData = {
  columns: Array<ColumnInstance>;
  rows: Record<string, string>[];
  total: number;
};

const getColumnKeyAccessor = (header: string, isEmptyHeader: boolean) => {
  if (isEmptyHeader) {
    return header;
  }
  const headerString = header.replace(/[.]/g, '_');
  const keyHeader = convertToTitleCase(headerString);
  const accessor = `${keyHeader.charAt(0).toLowerCase()}${keyHeader.slice(
    1,
    keyHeader.length - 1,
  )}`;
  return accessor;
};

const normalizeTicketEventsData = (
  data: CsvContents,
  t: TFunction,
): NormalizeData => {
  const { contents = [] } = data;
  const total = contents.length - 1; // First row is a header row.

  if (contents.length > 0) {
    const headers = contents[0];
    const columns = headers.map((header, index) => {
      const columnHeader = isEmpty(header)
        ? `${t('ticket:evidenceNavigator.column')}_${index + 1}`
        : header;
      return {
        Header: columnHeader,
        accessor: getColumnKeyAccessor(columnHeader, isEmpty(header)),
        sortable: true,
        width: 'auto',
      } as unknown as ColumnInstance;
    });
    const dataRows =
      contents.length > 1 ? contents.slice(1, contents.length) : [];
    const rows = dataRows.map((row) => {
      const obj: Record<string, string> = {};
      for (let i = 0; i < row.length; i++) {
        const columnHeader = isEmpty(headers[i])
          ? `${t('ticket:evidenceNavigator.column')}_${i + 1}`
          : headers[i];
        const key = getColumnKeyAccessor(columnHeader, isEmpty(headers[i]));
        obj[key] = row[i];
      }
      return obj;
    });
    return { columns, rows, total };
  }
  return { columns: [], rows: [], total: 0 };
};

export const useTicketEventsData = (
  params: TicketEventsParamType,
  t: TFunction,
): UseQueryResult<NormalizeData, ResponseError> =>
  useTicketEvents<NormalizeData>(params, t, normalizeTicketEventsData);
